import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, FileText, CheckCircle, ArrowRight, Shield, Globe2, Sparkles, Languages, Star, MousePointer2, Check, TableProperties, Bot, Bookmark, Flame, Target, Activity, Users, Trophy, Layers, LineChart, Briefcase, Wrench, ShoppingCart, ShieldCheck } from 'lucide-react';
import Lottie from 'lottie-react';
import { track } from '../../services/analytics';

const TRANSLATION_SLIDES = [
  { lang: 'Tiếng Việt', abbr: 'VN', title: 'Tổng quan Tài chính Q3', rev: 'Doanh thu', profit: 'Lợi nhuận ròng', growth: 'Tăng trưởng', metric: 'Chỉ số', download: 'Tải file dịch (.docx)', vals: ['$45,0M', '$62,5M', '$8,2M', '$14,5M'], desc: 'Quý 3 chứng kiến sự gia tăng đáng kể trong các luồng doanh thu cốt lõi. Chiến lược mở rộng đã mang lại kết quả tích cực.', legends: ['Lốp xe', 'Cảm biến', 'Khác'], smart: ['Bán sỉ OEM', 'Phụ tùng', 'Bán trực tuyến'], color: 'from-blue-500 to-indigo-500' },
  { lang: 'English', abbr: 'EN', title: 'Q3 Financial Overview', rev: 'Revenue', profit: 'Net Profit', growth: 'Growth', metric: 'Metric', download: 'Download file (.docx)', vals: ['$45.0M', '$62.5M', '$8.2M', '$14.5M'], desc: 'The third quarter saw a remarkable increase in core revenue streams. Our expansion strategy yielded highly positive results.', legends: ['Tires', 'Sensors', 'Other'], smart: ['OEM Sales', 'Aftermarket', 'Online Direct'], color: 'from-emerald-500 to-teal-500' },
  { lang: '中文', abbr: 'CN', title: '第三季度财务概览', rev: '收入', profit: '净利润', growth: '增长', metric: '指标', download: '下载翻译文件 (.docx)', vals: ['$45.0M', '$62.5M', '$8.2M', '$14.5M'], desc: '第三季度核心收入流显著增加。我们的扩张战略取得了非常积极的成果。', legends: ['轮胎', '传感器', '其他'], smart: ['OEM 销售', '售后市场', '在线直销'], color: 'from-red-500 to-rose-600' },
  { lang: 'العربية', abbr: 'AR', title: 'النظرة المالية للربع الثالث', rev: 'الإيرادات', profit: 'صافي الربح', growth: 'النمو', metric: 'مقياس', download: 'تنزيل الملف المترجم (.docx)', vals: ['$45.0M', '$62.5M', '$8.2M', '$14.5M'], desc: 'شهد الربع الثالث زيادة ملحوظة في مصادر الإيرادات الأساسية. أسفرت استراتيجية التوسع لدينا عن نتائج إيجابية للغاية.', legends: ['إطارات', 'أجهزة استشعار', 'أخرى'], smart: ['مبيعات OEM', 'ما بعد البيع', 'البيع المباشر عبر الإنترنت'], color: 'from-indigo-600 to-blue-700' },
  { lang: '日本語', abbr: 'JP', title: '第3四半期 財務概要', rev: '収益', profit: '純利益', growth: '成長', metric: '指標', download: '翻訳済みファイル (.docx)', vals: ['$45.0M', '$62.5M', '$8.2M', '$14.5M'], desc: '第3四半期は主要な収益源が著しく増加しました。当社の拡大戦略は非常に良い結果をもたらしました。', legends: ['タイヤ', 'センサー', 'その他'], smart: ['OEM販売', 'アフターマーケット', 'オンライン直販'], color: 'from-rose-500 to-pink-500' },
  { lang: '한국어', abbr: 'KR', title: '3분기 재무 개요', rev: '수익', profit: '순이익', growth: '성장', metric: '지표', download: '번역된 파일 다운로드 (.docx)', vals: ['$45.0M', '$62.5M', '$8.2M', '$14.5M'], desc: '3분기에는 핵심 수익원이 눈에 띄게 증가했습니다. 우리의 확장 전략은 매우 긍정적인 결과를 낳았습니다.', legends: ['타이어', '센서', '기타'], smart: ['OEM 판매', '애프터마켓', '온라인 직판'], color: 'from-amber-500 to-orange-500' },
  { lang: 'Français', abbr: 'FR', title: 'Aperçu Financier T3', rev: 'Revenus', profit: 'Bénéfice Net', growth: 'Croissance', metric: 'Métrique', download: 'Télécharger le fichier (.docx)', vals: ['$45,0M', '$62,5M', '$8,2M', '$14,5M'], desc: 'Le troisième trimestre a vu une augmentation remarquable des revenus. Notre stratégie d\'expansion a donné de bons résultats.', legends: ['Pneus', 'Capteurs', 'Autre'], smart: ['Ventes OEM', 'Marché Secondaire', 'Vente Directe'], color: 'from-teal-600 to-emerald-600' },
];
interface LandingPageProps {
  onSignup: () => void;
  onLogin: () => void;
}

const LandingPage: React.FC<LandingPageProps> = ({ onSignup, onLogin }) => {
  const navigate = useNavigate();

  const handleSignup = (source: string) => () => {
    track('cta_signup_click', { source });
    onSignup();
  };
  const handleLogin = (source: string) => () => {
    track('auth_google_start', { source });
    onLogin();
  };
  const [slideIndex, setSlideIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [fireAnimationData, setFireAnimationData] = useState(null);

  useEffect(() => {
    fetch('/fire.json')
      .then(res => res.json())
      .then(data => setFireAnimationData(data))
      .catch(err => console.error("Lỗi load Lottie fire.json:", err));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setSlideIndex((prev) => (prev + 1) % (TRANSLATION_SLIDES.length + 1));
        setIsTransitioning(false);
      }, 500);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const isFinalBadge = slideIndex === TRANSLATION_SLIDES.length;
  const currentSlide = TRANSLATION_SLIDES[slideIndex % TRANSLATION_SLIDES.length];

  return (
    <div className="h-screen overflow-x-hidden lg:overflow-hidden overflow-y-auto w-full bg-[#f8fafc] text-slate-900 selection:bg-emerald-200 flex flex-col relative">
      
      {/* EMERALD BACKGROUND BLOBS */}
      <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[50%] bg-emerald-400/20 rounded-full mix-blend-multiply filter blur-[100px] animate-blob pointer-events-none z-0"></div>
      <div className="absolute top-[20%] right-[-5%] w-[35%] h-[45%] bg-teal-400/20 rounded-full mix-blend-multiply filter blur-[100px] animate-blob pointer-events-none z-0" style={{ animationDelay: '2s' }}></div>
      <div className="absolute bottom-[-20%] left-[20%] w-[50%] h-[50%] bg-cyan-400/20 rounded-full mix-blend-multiply filter blur-[100px] animate-blob pointer-events-none z-0" style={{ animationDelay: '4s' }}></div>

      {/* Navbar */}
      <nav className="flex-none px-6 lg:px-12 py-4 lg:py-5 flex items-center justify-between z-50 relative bg-white/40 backdrop-blur-xl border-b border-white/50 shadow-sm">
        <div className="flex items-center gap-3">
          <img src="/icons/logo-64.png" alt="DịchTàiLiệu.VN Logo" className="w-10 h-10" />
          <span className="font-black text-2xl tracking-tight text-slate-800">DịchTàiLiệu<span className="text-emerald-600">.VN</span></span>
        </div>
        
        <div className="hidden md:flex items-center gap-6">
          <a href="/pricing" className="text-sm font-bold text-slate-600 hover:text-emerald-600 transition-colors">Bảng giá</a>
          <a href="/help" className="text-sm font-bold text-slate-600 hover:text-emerald-600 transition-colors">Trợ giúp</a>
          <a href="/privacy" className="text-sm font-bold text-slate-600 hover:text-emerald-600 transition-colors">Privacy</a>
          <a href="/tos" className="text-sm font-bold text-slate-600 hover:text-emerald-600 transition-colors">Điều khoản</a>
          <div className="w-px h-6 bg-slate-300/50 mx-2"></div>
          <button onClick={handleLogin('nav')} className="text-sm font-bold text-slate-700 hover:text-emerald-600 transition-colors">
            Đăng nhập
          </button>
          <button onClick={handleSignup('nav')} className="px-6 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl text-sm font-bold hover:shadow-lg hover:shadow-emerald-300 hover:scale-105 transition-all active:scale-95">
            Dùng thử miễn phí
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow flex flex-col lg:flex-row items-center w-full max-w-[1500px] mx-auto px-6 lg:px-12 gap-8 lg:gap-12 relative lg:h-full z-10 py-10 lg:py-0">
        
        {/* LEFT COLUMN: Copy & Features */}
        <div className="w-full lg:w-[55%] flex flex-col justify-center items-center lg:items-start text-center lg:text-left">
          
          {/* Badge / Kicker */}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-100/50 rounded-full text-[11px] font-bold uppercase tracking-wider text-emerald-700 w-fit mb-6 shadow-sm">
            <Sparkles className="w-3.5 h-3.5 text-emerald-500" />
            Giải pháp AI dịch tài liệu chuyên ngành — Audit · Legal · FDI · HR
          </div>

          {/* Headline */}
          <h1 className="text-4xl lg:text-[54px] font-extrabold text-slate-900 leading-[1.15] tracking-tight mb-4 lg:mb-6">
            Dịch báo cáo 200 trang <br />
            trong 20 phút bằng AI <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-teal-500">Giữ nguyên 100% định dạng</span>
          </h1>
          
          {/* Description */}
          <p className="text-lg text-slate-500 mb-8 max-w-2xl leading-relaxed">
            Chấm dứt việc copy từng đoạn lên AI rồi hì hục căn chỉnh lại bảng biểu, dấu chấm dấu phẩy khi dịch. Chỉ cần tải file <strong className="text-[#1A5DC4] font-bold">Word</strong> / <strong className="text-[#D13426] font-bold">PowerPoint</strong> lên, <strong className="text-slate-800 font-semibold">Dịch Tài Liệu</strong> sẽ trả về bản dịch hoàn hảo với cấu trúc nguyên bản.
          </p>

          {/* ACTION & PRIVACY BLOCK */}
          <div className="flex flex-col sm:flex-row items-center lg:items-start gap-6 sm:gap-8 mb-8 lg:mb-12">
            <div className="flex flex-col w-full sm:w-auto gap-3 shrink-0">
              <button
                onClick={handleSignup('hero')}
                className="group relative px-8 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all active:scale-95 flex items-center gap-2.5 text-base w-full sm:w-auto justify-center overflow-hidden"
              >
                <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out"></div>
                <span className="relative flex items-center gap-2">
                  {fireAnimationData ? (
                    <Lottie 
                      animationData={fireAnimationData} 
                      loop={true} 
                      className="w-[22px] h-[22px] -ml-1 mb-1"
                      style={{ filter: 'hue-rotate(140deg) brightness(1.2) saturate(1.2)' }}
                    />
                  ) : (
                    <Flame className="w-5 h-5 text-emerald-100 fill-emerald-100 animate-pulse mb-1" />
                  )}
                  <span className="font-bold">Nhận ngay 200k credit</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform" />
                </span>
              </button>
              
              <div className="flex items-center justify-center sm:justify-start gap-2.5 pl-1">
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">Hỗ trợ:</span>
                <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-[#2B7CFA]/20 bg-[#2B7CFA]/5 shadow-sm transition-all hover:bg-[#2B7CFA]/10 cursor-default">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/e/e8/Microsoft_Office_Word_%282025%E2%80%93present%29.svg/960px-Microsoft_Office_Word_%282025%E2%80%93present%29.svg.png" alt="Word" className="w-5 h-5 object-contain drop-shadow-sm" />
                  <span className="text-[11px] font-extrabold text-[#1A5DC4] tracking-tight">Word</span>
                </div>
                <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-md border border-[#F57635]/20 bg-[#F57635]/5 shadow-sm transition-all hover:bg-[#F57635]/10 cursor-default">
                  <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/df/Microsoft_Office_PowerPoint_%282025%E2%80%93present%29.svg/1280px-Microsoft_Office_PowerPoint_%282025%E2%80%93present%29.svg.png" alt="PowerPoint" className="w-5 h-5 object-contain drop-shadow-sm" />
                  <span className="text-[11px] font-extrabold text-[#D13426] tracking-tight">PowerPoint</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-start sm:pt-2">
              <div className="grid grid-cols-2 sm:grid-cols-2 gap-x-5 gap-y-3">
                <div className="flex items-center gap-2 text-[12px] font-medium text-slate-600">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" /> Không lưu nội dung
                </div>
                <div className="flex items-center gap-2 text-[12px] font-medium text-slate-600">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" /> Không train AI
                </div>
                <div className="flex items-center gap-2 text-[12px] font-medium text-slate-600">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" /> Không chia sẻ bên thứ 3
                </div>
                <div className="flex items-center gap-2 text-[12px] font-medium text-slate-600">
                  <Check className="w-4 h-4 text-emerald-500 shrink-0" /> Chỉ lưu trên máy bạn
                </div>
              </div>
            </div>
          </div>

          {/* FEATURES BLOCK */}
          {/* FEATURES BLOCK */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-8 mb-6 w-full max-w-2xl bg-white/40 p-4 sm:p-6 rounded-2xl border border-emerald-50/50 shadow-sm backdrop-blur-sm">
            <div className="flex gap-3.5 items-start">
              <div className="w-9 h-9 rounded-xl bg-emerald-100/60 flex items-center justify-center shrink-0 border border-emerald-100">
                <Bookmark className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="pt-0.5">
                <h3 className="font-bold text-slate-900 text-sm mb-0.5">Hệ Thống Training Thuật Ngữ</h3>
                <p className="text-[13px] text-slate-500 leading-snug">Sử dụng một Agent chuyên biệt để trích xuất và cố định thuật ngữ, đảm bảo tính nhất quán tuyệt đối cho tài liệu.</p>
              </div>
            </div>
            
            <div className="flex gap-3.5 items-start">
              <div className="w-9 h-9 rounded-xl bg-emerald-100/60 flex items-center justify-center shrink-0 border border-emerald-100">
                <ShieldCheck className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="pt-0.5">
                <h3 className="font-bold text-slate-900 text-sm mb-0.5">Tích Hợp Agent Kiểm Tra</h3>
                <p className="text-[13px] text-slate-500 leading-snug">AI Agent hoạt động độc lập tự động kiểm tra chéo (cross-check) sau dịch để đảm bảo độ chính xác hoàn hảo.</p>
              </div>
            </div>
            
            <div className="flex gap-3.5 items-start">
              <div className="w-9 h-9 rounded-xl bg-emerald-100/60 flex items-center justify-center shrink-0 border border-emerald-100">
                <Bot className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="pt-0.5">
                <h3 className="font-bold text-slate-900 text-sm mb-0.5">Gemini 3.1 & Chi Phí Tối Ưu</h3>
                <p className="text-[13px] text-slate-500 leading-snug">Công nghệ Gemini 3.1 mới nhất với chi phí rẻ hơn nhiều so với việc tự đưa tài liệu vào Gemini hay Claude.</p>
              </div>
            </div>
            
            <div className="flex gap-3.5 items-start">
              <div className="w-9 h-9 rounded-xl bg-emerald-100/60 flex items-center justify-center shrink-0 border border-emerald-100">
                <Languages className="w-4 h-4 text-emerald-600" />
              </div>
              <div className="pt-0.5">
                <h3 className="font-bold text-slate-900 text-sm mb-0.5">Hỗ Trợ 100+ Ngôn Ngữ</h3>
                <p className="text-[13px] text-slate-500 leading-snug">Dịch chéo mượt mà giữa hàng trăm ngôn ngữ trên thế giới với độ chuẩn xác như người bản xứ.</p>
              </div>
            </div>
          </div>



        </div>

        {/* RIGHT COLUMN: The Glowing Carousel */}
        <div className="w-full lg:w-[45%] flex items-center justify-center relative lg:h-full">
          
          {/* Big Glowing Backdrop */}
          <div className="absolute w-[80%] h-[80%] bg-emerald-400 rounded-full blur-[100px] opacity-10 pointer-events-none"></div>

          {/* Document Container */}
          <div className="w-full max-w-[600px] bg-white/90 backdrop-blur-xl rounded-[2.5rem] shadow-2xl shadow-emerald-900/5 border border-white p-3 relative z-10">
            <div className="bg-[#fcfdfd] rounded-[2rem] border border-slate-100 overflow-hidden h-[640px] flex flex-col relative shadow-inner">
              
              {/* Header */}
              <div className="h-16 bg-white border-b border-slate-100 flex items-center justify-between px-6 flex-none">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100">
                    <FileText className="w-5 h-5 text-blue-600" />
                  </div>
                  <div className="flex flex-col">
                    <span className="font-bold text-slate-800 text-sm">Bao_cao_Tai_chinh.docx</span>
                    <span className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest flex items-center gap-1">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                      Đang xử lý dịch thuật
                    </span>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <div className="w-3 h-3 rounded-full bg-red-400 border border-red-500"></div>
                  <div className="w-3 h-3 rounded-full bg-amber-400 border border-amber-500"></div>
                  <div className="w-3 h-3 rounded-full bg-emerald-400 border border-emerald-500"></div>
                </div>
              </div>

              {/* Body */}
              <div className="p-8 flex-grow relative overflow-hidden bg-[#fcfdfd]">
                
                {/* Floating Language Badge */}
                <div className="absolute top-4 right-4 z-20">
                  <div className={`px-4 py-1.5 rounded-full text-[11px] font-black text-white shadow-md flex items-center gap-1.5 transition-all duration-500 ${isFinalBadge ? 'bg-gradient-to-r from-emerald-600 to-teal-600 scale-105' : `bg-gradient-to-r ${currentSlide.color}`}`}>
                    {isFinalBadge ? <Sparkles className="w-3.5 h-3.5" /> : <Languages className="w-3.5 h-3.5" />}
                    {isFinalBadge ? '100+ NGÔN NGỮ KHÁC' : currentSlide.lang.toUpperCase()}
                  </div>
                </div>

                <div className={`w-full transition-all duration-500 flex flex-col gap-8 ${isTransitioning ? 'opacity-30 blur-[2px]' : 'opacity-100'} ${isFinalBadge ? 'blur-[6px] opacity-40 grayscale-[30%] select-none' : ''}`}>
                  
                  {/* Title Block */}
                  {/* Title & Text Block */}
                  <div className="flex flex-col gap-4">
                    <h2 className={`text-[24px] font-black tracking-tight transition-colors duration-500 ${isFinalBadge ? 'text-emerald-700' : 'text-slate-800'}`}>
                       {isFinalBadge ? '100+ Ngôn Ngữ Khác' : currentSlide.title}
                    </h2>
                    <div className="pl-4 border-l-2 border-emerald-400">
                      <p className="text-[13px] leading-relaxed text-slate-500 font-medium italic">
                        "{isFinalBadge ? 'The third quarter saw a remarkable increase in core revenue streams. Our expansion strategy yielded highly positive results.' : currentSlide.desc}"
                      </p>
                    </div>
                  </div>

                  {/* Middle Block: Pie Chart & Smart Art */}
                  <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:h-[150px]">
                    {/* Horizontal Bar Chart Widget */}
                    <div className="w-full sm:w-[32%] h-[120px] sm:h-full py-4 px-4 bg-white rounded-2xl border border-slate-200 shadow-sm relative flex flex-col justify-center gap-3.5">
                      {/* Bar 1: Tires */}
                      <div className="flex flex-col gap-1.5 w-full">
                        <div className="flex justify-between items-end">
                          <span className="text-[9px] font-bold text-slate-700 uppercase tracking-tight">{isFinalBadge ? 'Tires' : currentSlide.legends[0]}</span>
                          <span className="text-[9px] font-black text-[#34d399]">45%</span>
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#34d399] rounded-full w-[45%] transition-all duration-700"></div>
                        </div>
                      </div>

                      {/* Bar 2: Sensors */}
                      <div className="flex flex-col gap-1.5 w-full">
                        <div className="flex justify-between items-end">
                          <span className="text-[9px] font-bold text-slate-700 uppercase tracking-tight">{isFinalBadge ? 'Sensors' : currentSlide.legends[1]}</span>
                          <span className="text-[9px] font-black text-[#60a5fa]">30%</span>
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-[#60a5fa] rounded-full w-[30%] transition-all duration-700 delay-100"></div>
                        </div>
                      </div>

                      {/* Bar 3: Other */}
                      <div className="flex flex-col gap-1.5 w-full">
                        <div className="flex justify-between items-end">
                          <span className="text-[9px] font-bold text-slate-500 uppercase tracking-tight">{isFinalBadge ? 'Other' : currentSlide.legends[2]}</span>
                          <span className="text-[9px] font-black text-slate-400">25%</span>
                        </div>
                        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-slate-300 rounded-full w-[25%] transition-all duration-700 delay-200"></div>
                        </div>
                      </div>
                    </div>

                    {/* Tree/Hub Flow Chart Widget */}
                    <div className="flex-1 h-[140px] sm:h-full bg-slate-50 rounded-2xl border border-slate-200 shadow-sm relative flex items-center justify-between overflow-hidden p-6">
                      
                      {/* Grid Background */}
                      <div className="absolute inset-0 z-0 opacity-30 pointer-events-none" style={{ backgroundImage: 'linear-gradient(#cbd5e1 1px, transparent 1px), linear-gradient(90deg, #cbd5e1 1px, transparent 1px)', backgroundSize: '20px 20px' }}></div>
                      
                      {/* Left Node Glow */}
                      <div className="absolute left-6 top-1/2 -translate-y-1/2 w-28 h-28 bg-cyan-200/40 rounded-full blur-2xl pointer-events-none z-0"></div>

                      {/* SVG Connecting Lines */}
                      <svg className="absolute inset-0 w-full h-full pointer-events-none z-0" viewBox="0 0 100 100" preserveAspectRatio="none">
                        {/* Top Line */}
                        <path d="M 15,50 C 40,50 40,24 67,24" fill="none" stroke="#10b981" strokeWidth="3" vectorEffect="non-scaling-stroke" opacity="0.3" />
                        {/* Middle Line */}
                        <path d="M 15,50 L 67,50" fill="none" stroke="#10b981" strokeWidth="3" vectorEffect="non-scaling-stroke" opacity="0.3" />
                        {/* Bottom Line */}
                        <path d="M 15,50 C 40,50 40,76 67,76" fill="none" stroke="#10b981" strokeWidth="3" vectorEffect="non-scaling-stroke" opacity="0.3" />
                      </svg>

                      {/* Left Node (Hub) */}
                      <div className="relative z-10 w-16 h-16 bg-white rounded-full shadow-lg border-2 border-white flex items-center justify-center shrink-0 ml-0" title="Revenue Streams">
                        <div className="w-10 h-10 bg-emerald-50 rounded-full flex items-center justify-center">
                          <LineChart className="w-5 h-5 text-emerald-500" />
                        </div>
                      </div>

                      {/* Right Nodes Stack */}
                      <div className="relative z-10 flex flex-col justify-center gap-2.5 h-full w-[140px] mr-0">
                        
                        {/* Node 1: OEM */}
                        <div className="w-full bg-emerald-50/80 rounded border border-emerald-100 shadow-sm flex items-center p-1.5 px-2 gap-2 transition-all duration-300 hover:translate-x-1 cursor-pointer">
                          <div className="w-5 h-5 bg-emerald-100/50 rounded-full flex items-center justify-center shrink-0">
                            <Briefcase className="w-3 h-3 text-emerald-600" />
                          </div>
                          <span className="text-[10px] font-bold text-slate-800 truncate">{isFinalBadge ? 'OEM Sales' : currentSlide.smart[0]}</span>
                        </div>

                        {/* Node 2: Aftermarket */}
                        <div className="w-full bg-emerald-50/80 rounded border border-emerald-100 shadow-sm flex items-center p-1.5 px-2 gap-2 transition-all duration-300 hover:translate-x-1 cursor-pointer">
                          <div className="w-5 h-5 bg-emerald-100/50 rounded-full flex items-center justify-center shrink-0">
                            <Wrench className="w-3 h-3 text-emerald-600" />
                          </div>
                          <span className="text-[10px] font-bold text-slate-800 truncate">{isFinalBadge ? 'Aftermarket' : currentSlide.smart[1]}</span>
                        </div>

                        {/* Node 3: Online */}
                        <div className="w-full bg-emerald-50/80 rounded border border-emerald-100 shadow-sm flex items-center p-1.5 px-2 gap-2 transition-all duration-300 hover:translate-x-1 cursor-pointer">
                          <div className="w-5 h-5 bg-emerald-100/50 rounded-full flex items-center justify-center shrink-0">
                            <ShoppingCart className="w-3 h-3 text-emerald-600" />
                          </div>
                          <span className="text-[10px] font-bold text-slate-800 truncate">{isFinalBadge ? 'Online Direct' : currentSlide.smart[2]}</span>
                        </div>

                      </div>
                    </div>

                  </div>

                  {/* Bottom Block: Clean Data Table */}
                  <div className="w-full mt-4">
                    <div className="w-full rounded-xl overflow-hidden bg-white shadow-[0_2px_10px_-3px_rgba(0,0,0,0.08)] border border-slate-200/60">
                      <table className="w-full text-xs">
                        {/* Clean Header */}
                        <thead className="bg-slate-50/50 border-b border-slate-100">
                          <tr>
                            <th className="py-2.5 px-4 text-left text-[10px] font-bold text-slate-400 uppercase tracking-widest w-[35%]">{isFinalBadge ? 'Metric' : currentSlide.metric}</th>
                            <th className="py-2.5 px-4 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest w-[20%]">FY 23</th>
                            <th className="py-2.5 px-4 text-right text-[10px] font-bold text-slate-700 uppercase tracking-widest w-[20%] bg-slate-100/50">FY 24</th>
                            <th className="py-2.5 px-4 text-right text-[10px] font-bold text-slate-400 uppercase tracking-widest w-[25%]">{isFinalBadge ? 'Growth' : currentSlide.growth}</th>
                          </tr>
                        </thead>
                        {/* Clean Rows */}
                        <tbody className="divide-y divide-slate-50">
                          {/* Row 1 */}
                          <tr className="group hover:bg-slate-50/50 transition-colors">
                            <td className="py-3 px-4 font-semibold text-slate-800 flex items-center">
                              {isFinalBadge ? 'Revenue' : currentSlide.rev}
                            </td>
                            <td className="py-3 px-4 text-right text-slate-500 font-mono text-[11px]">{isFinalBadge ? '$45.0M' : currentSlide.vals[0]}</td>
                            <td className="py-3 px-4 text-right text-slate-900 font-bold font-mono text-[11px] bg-slate-50/30 group-hover:bg-transparent transition-colors">{isFinalBadge ? '$62.5M' : currentSlide.vals[1]}</td>
                            <td className="py-3 px-4 text-right">
                               <span className="inline-flex items-center bg-emerald-50 text-emerald-600 border border-emerald-200/60 px-2 py-0.5 rounded-md font-mono font-bold text-[10px]">+37%</span>
                            </td>
                          </tr>
                          {/* Row 2 */}
                          <tr className="group hover:bg-slate-50/50 transition-colors">
                            <td className="py-3 px-4 font-semibold text-slate-800 flex items-center">
                              {isFinalBadge ? 'Net Profit' : currentSlide.profit}
                            </td>
                            <td className="py-3 px-4 text-right text-slate-500 font-mono text-[11px]">{isFinalBadge ? '$8.2M' : currentSlide.vals[2]}</td>
                            <td className="py-3 px-4 text-right text-slate-900 font-bold font-mono text-[11px] bg-slate-50/30 group-hover:bg-transparent transition-colors">{isFinalBadge ? '$14.5M' : currentSlide.vals[3]}</td>
                            <td className="py-3 px-4 text-right">
                               <span className="inline-flex items-center bg-emerald-50 text-emerald-600 border border-emerald-200/60 px-2 py-0.5 rounded-md font-mono font-bold text-[10px]">+76%</span>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Overlay for Final Slide */}
                <div className={`absolute inset-0 flex flex-col items-center justify-center z-30 transition-all duration-500 ${isFinalBadge ? 'opacity-100 pointer-events-auto backdrop-blur-[2px]' : 'opacity-0 pointer-events-none'}`}>
                   <div className="w-24 h-24 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-2xl shadow-emerald-500/40 mb-6 animate-bounce-slow">
                     <Languages className="w-12 h-12 text-white" />
                   </div>
                   <h2 className="text-3xl font-black text-slate-800 tracking-tight text-center px-4">Sẵn sàng vươn ra toàn cầu</h2>
                   <h2 className="text-[3.5rem] leading-none font-black bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mt-3 text-center px-4 pb-2">+100 ngôn ngữ</h2>
                   <p className="mt-4 text-sm font-semibold text-slate-500 uppercase tracking-widest">đang chờ bạn khám phá</p>
                </div>

                {/* Laser scan line moving up and down the document to simulate processing */}
                {!isFinalBadge && (
                  <div key={`scan-${slideIndex}`} className={`absolute left-0 right-0 h-[3px] bg-gradient-to-r ${currentSlide.color} shadow-[0_0_20px_rgba(16,185,129,0.8)] animate-scan-fast pointer-events-none z-10`} style={{ top: '0' }}></div>
                )}
              </div>

              {/* Dots */}
              <div className="absolute bottom-6 left-0 right-0 flex justify-center gap-2 pointer-events-none z-20">
                {TRANSLATION_SLIDES.map((_, i) => (
                  <div key={i} className={`h-2 transition-all duration-500 rounded-full ${slideIndex === i ? 'w-8 bg-emerald-600' : 'w-2 bg-slate-300'}`}></div>
                ))}
              </div>

            </div>
          </div>

        </div>
      </main>

      {/* PAGE 2: FOUNDER'S QUOTE SECTION (Tạm ẩn) */}
      <section className="hidden snap-start min-h-screen w-full bg-slate-50/50 border-t border-slate-200/50 py-24 px-6 lg:px-12 relative z-10 flex-col items-center justify-center backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 border-4 border-white shadow-md mx-auto mb-6 flex items-center justify-center overflow-hidden">
             <img src="https://ui-avatars.com/api/?name=Founder&background=10b981&color=fff&bold=true" alt="Founder" className="w-full h-full object-cover" />
          </div>
          <div className="relative inline-block">
            <div className="absolute -top-6 -left-8 text-6xl text-emerald-200 opacity-60 font-serif">"</div>
            <p className="text-[19px] md:text-[21px] text-slate-700 leading-[1.7] font-medium mb-6 relative z-10">
              App này ban đầu được tôi tạo ra với mục đích cá nhân là hỗ trợ dịch báo cáo thường niên cho công ty niêm yết năm 2026. Trước đó, dù có 10 nhân sự IELTS &gt; 7.0 và ChatGPT hỗ trợ, chúng tôi vẫn mất tới 1 tháng chỉ vì phải loay hoay sửa văn phong không hợp, đồng bộ thuật ngữ, kiểm tra nội dung và hì hục format lại file.
            </p>
            <p className="text-[19px] md:text-[21px] text-slate-700 leading-[1.7] font-medium mb-8 relative z-10">
              Từ những vấn đề đó, tôi build ra app này với mục tiêu: dịch và giữ nguyên 100% định dạng, có hệ thống từ điển thuật ngữ cá nhân, và tích hợp AI kiểm tra nội dung dịch. Và đó chính là sự hình thành của app này, mang theo mong muốn giúp mọi người giải thoát khỏi mớ công việc tay chân ám ảnh giống như tôi!
            </p>
            <div className="absolute -bottom-10 -right-8 text-6xl text-emerald-200 opacity-60 font-serif rotate-180">"</div>
          </div>
          <div className="flex flex-col items-center mt-4">
            <span className="text-sm font-bold text-slate-800 uppercase tracking-wider">Chia sẻ từ Founder</span>
            <span className="text-xs text-emerald-600 font-semibold mt-1">Người kiến tạo Dịch Tài Liệu</span>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 8s infinite;
        }
        @keyframes scan-fast {
          0% { top: 0; opacity: 0; }
          10% { opacity: 1; }
          90% { opacity: 1; }
          100% { top: 100%; opacity: 0; }
        }
        .animate-scan-fast {
          animation: scan-fast 4s ease-in-out infinite;
        }
        @keyframes bounce-slow {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }
        html, body, #root {
          height: 100%;
          overflow: hidden;
          margin: 0;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
